export const SECTION_LIBRARY = [
  {id:'hero',name:'Hero',description:'Headline, image and a button',symbol:'▰'},
  {id:'products',name:'Products',description:'Live meal items, plans, passes or catering',symbol:'▦'},
  {id:'text',name:'Text',description:'A heading and your story',symbol:'T'},
  {id:'image',name:'Image',description:'A full-width photograph and caption',symbol:'▧'},
  {id:'faq',name:'FAQ',description:'Three editable questions and answers',symbol:'?'},
  {id:'callout',name:'Call to action',description:'A message with a destination button',symbol:'↗'},
];

const clone = (v) => JSON.parse(JSON.stringify(v));
const text = (value) => ({type:'text',value});
const el = (tag,children,className='') => ({type:'element',tag,children,className});
const unique = () => `section-${crypto.randomUUID().slice(0,8)}`;

export function materializePackage(pkg, edits) {
  if (!edits.layout) return pkg;
  return {...pkg, pages:{...pkg.pages,...edits.layout.pages}, manifest:{...pkg.manifest,settings:{...pkg.manifest.settings,...edits.layout.settings}},bindings:{...pkg.bindings,collections:{...pkg.bindings.collections,...edits.layout.collections}}};
}

export function changeSections(source, edits, pageKey, operation, selectedId, template) {
  const pkg = materializePackage(source,edits);
  const next = clone(edits);
  const layout = next.layout ||= {pages:{},settings:{},collections:{}};
  layout.pages ||= {}; layout.settings ||= {}; layout.collections ||= {};
  const page = clone(pkg.pages[pageKey]);
  const order = clone(edits.sections[pageKey] || page.sections.map(({id,enabled}) => ({id,enabled})));
  const index = order.findIndex((r) => r.id===selectedId);
  if (operation!=='add' && index<0) throw new Error('Select a section first.');
  let section;
  if (operation==='remove') {
    if (page.sections.length<=1) throw new Error('Keep at least one section on each page.');
    page.sections = page.sections.filter((s) => s.id!==selectedId);
    order.splice(index,1);
    Object.keys(next.blocks || {}).forEach((p) => {if(p.startsWith(`${pageKey}/${selectedId}/`)) delete next.blocks[p];});
    const repair=(node) => {
      if(node.action==='navigate.section' && node.target===selectedId) {node.action='navigate.page';node.target=pageKey;}
      (node.children || []).forEach(repair);if(node.template) repair(node.template);if(node.empty) repair(node.empty);
    };
    page.sections.forEach((s) => repair(s.root));
    Object.entries(next.blocks || {}).forEach(([p,v]) => {if(p.startsWith(`${pageKey}/`) && v.target===selectedId) next.blocks[p]={...v,action:'navigate.page',target:pageKey};});
  } else {
    if (operation!=='replace' && page.sections.length>=60) throw new Error('A page can contain up to 60 sections.');
    const id = operation==='replace' ? selectedId : unique();
    if (operation==='duplicate') {
      const original=page.sections.find((s) => s.id===selectedId);
      if (pkg.native && original.root.className===`plateos-native-${selectedId}`) throw new Error('Add another section from the library. Built-in PlateOS sections keep their existing controls.');
      section=clone(original); section.id=id; section.label=`${original.label} copy`.slice(0,100); section.reorderable=true;
      const aliases={};
      const copyValues=(value) => {
        if (!value || typeof value!=='object') return;
        if (value.setting) {
          const old=value.setting;
          if (!aliases[old]) {
            const key=`${id}_${Object.keys(aliases).length}`; aliases[old]=key;
            layout.settings[key]={...clone(pkg.manifest.settings[old]),default:edits.settings[old] ?? pkg.manifest.settings[old].default};
          }
          value.setting=aliases[old];
        }
        if (value.attrs?.id) value.attrs.id=`${id}-${value.attrs.id}`;
        if (value.action==='navigate.section' && value.target===selectedId) value.target=id;
        Object.values(value).forEach(copyValues);
      };
      copyValues(section.root);
      Object.entries(edits.blocks || {}).forEach(([p,v]) => {if(p.startsWith(`${pageKey}/${selectedId}/`)) (next.blocks ||= {})[p.replace(`${pageKey}/${selectedId}/`,`${pageKey}/${id}/`)]=clone(v);});
    } else {
      const title=SECTION_LIBRARY.find((s) => s.id===template)?.name;
      if (!title) throw new Error('Choose a section from the library.');
      const image={type:'image',src:'',alt:'',className:'pw-image'};
      const button={type:'action',action:'navigate.page',target:pkg.manifest.entry,className:'pw-button',children:[text('Explore')]};
      const heading=el('h2',[text(template==='faq' ? 'Frequently asked questions' : 'Your headline')],'pw-heading');
      let children=[heading,el('p',[text('Add your own description here.')],'pw-body')];
      if (template==='hero') children=[el('div',[...children,button]),image];
      if (template==='image') children=[image,el('p',[text('Add a caption for your image.')],'pw-body')];
      if (template==='callout') children.push(button);
      if (template==='faq') children=[heading,...[1,2,3].map((n) => el('details',[el('summary',[text(`Question ${n}`)]),el('p',[text('Add your answer here.')])],'pw-faq'))];
      if (template==='products') {
        const collection=`${id}_products`; layout.collections[collection]={kind:'meal_plan',selection:'all_available'};
        children=[el('h2',[text('Explore our products')],'pw-heading'),el('div',[{type:'repeat',collection,as:'product',template:el('article',[
          {type:'image',src:{data:'product.image'},alt:{data:'product.title'},className:'pw-image'},
          el('h3',[text({data:'product.title'})]),el('p',[text({data:'product.description'})]),
          el('strong',[text({data:'product.price_label'})]),{type:'native',component:'productConfigurator',target:{data:'product.ref'},className:'pw-button'},
        ],'pw-product'),empty:el('p',[text('No products are available right now.')])}],'pw-grid')];
      }
      section={id,label:title,enabled:true,reorderable:true,root:el('div',children,`pw-section pw-${template}`)};
    }
    if(operation==='replace') {page.sections=page.sections.filter((s) => s.id!==selectedId);order.splice(index,1);Object.keys(next.blocks || {}).forEach((p) => {if(p.startsWith(`${pageKey}/${selectedId}/`)) delete next.blocks[p];});}
    page.sections.push(section);
    // Respect a fixed footer while inserting after the currently selected row.
    const footerIndex=order.findIndex((r) => /footer/i.test(r.id));
    const at=operation==='replace' ? index : index>=0 ? index+1 : footerIndex>=0 ? footerIndex : order.length;
    order.splice(at,0,{id:section.id,enabled:true});
  }
  // Freeze the new source order at the visible order; addresses use stable IDs.
  const byId=Object.fromEntries(page.sections.map((s) => [s.id,s]));
  page.sections=order.map((r) => byId[r.id]);
  layout.pages[pageKey]=page;
  next.sections={...next.sections,[pageKey]:order};
  // Drop generated fields once their section is removed, freeing the quota.
  const used=JSON.stringify({...pkg.pages,...layout.pages});
  Object.keys(layout.settings).forEach((k) => {if(!used.includes(`"${k}"`)) {delete layout.settings[k];delete next.settings[k];}});
  Object.keys(layout.collections).forEach((k) => {if(!used.includes(`"${k}"`)) {delete layout.collections[k];delete next.collections[k];}});
  return {edits:next,sectionId:section?.id};
}

export const libraryCSS = `
[data-plateos-site] .pw-section{padding:clamp(28px,5vw,72px) clamp(20px,6vw,90px);max-width:1500px;margin:auto;font:inherit}
[data-plateos-site] .pw-heading{font-size:clamp(28px,4vw,48px);line-height:1.15;margin:0 0 20px}
[data-plateos-site] .pw-body{font-size:18px;line-height:1.7;margin:16px 0}
[data-plateos-site] .pw-image{display:block;width:100%;max-height:600px;object-fit:cover;border-radius:16px}
[data-plateos-site] .pw-hero{display:grid;grid-template-columns:1fr 1fr;align-items:center;gap:36px}
[data-plateos-site] .pw-button{display:inline-flex;align-items:center;padding:14px 24px;margin-top:18px;border:1px solid currentColor;border-radius:10px;background:transparent;color:inherit;font:inherit}
[data-plateos-site] .pw-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:24px}
[data-plateos-site] .pw-product{display:flex;flex-direction:column;align-items:flex-start;gap:12px;padding:18px;border:1px solid #8884;border-radius:16px}
[data-plateos-site] .pw-product .pw-image{aspect-ratio:4/3;max-height:280px}
[data-plateos-site] .pw-faq{border-bottom:1px solid #8884;padding:18px 0;line-height:1.6}
[data-plateos-site] .pw-faq summary{font-weight:600;cursor:pointer}
[data-plateos-site] .pw-callout{text-align:center}
@media(max-width:700px){[data-plateos-site] .pw-hero,[data-plateos-site] .pw-grid{grid-template-columns:1fr}}
`;
